// Permissions in ra-rbac's format, checked with ra-rbac's semantics (ADR 0009).
// `canAccess` reproduces ra-core-ee's canAccessWithPermissions as its CODE
// behaves, which differs from its doc comments in places:
//   - `posts.*` matches `posts.title` but NOT `posts` (prefix `posts.`);
//   - any pattern ending in `*` is a prefix match (`post*` matches `posts`);
//   - an absent action, or action `*`, matches on resource alone and skips
//     the record check;
//   - a permission's `record` is only checked when a record is passed.
// Malformed entries (no string/array resource or action) never match and
// never throw (ADR 0009 rule 5); ra-rbac itself would throw or match them.
import { jwtDecode } from 'jwt-decode'

export interface Permission {
  action: string | string[]
  resource: string | string[]
  record?: Record<string, unknown>
  type?: 'allow' | 'deny'
}

export interface AccessCheck {
  permissions: readonly Permission[] | null | undefined
  resource: string
  action?: string | undefined
  record?: unknown
}

/** Decode the JWT `permissions` claim (`string | string[] | object[]`). Never throws. */
export function decodePermissions(token: string): Permission[] {
  try {
    const claim = jwtDecode<{ permissions?: unknown }>(token).permissions
    const list = Array.isArray(claim) ? claim : claim == null ? [] : [claim]
    return list.flatMap((entry): Permission[] => {
      if (typeof entry === 'string') {
        try {
          const parsed: unknown = JSON.parse(entry)
          return isPermission(parsed) ? [parsed] : []
        } catch {
          return []
        }
      }
      return isPermission(entry) ? [entry] : []
    })
  } catch {
    return []
  }
}

const isStringOrList = (x: unknown): x is string | string[] =>
  typeof x === 'string' || (Array.isArray(x) && x.every((i) => typeof i === 'string'))

function isPermission(value: unknown): value is Permission {
  if (typeof value !== 'object' || value === null) return false
  const v = value as Record<string, unknown>
  return isStringOrList(v.action) && isStringOrList(v.resource)
}

/** True when the permissions allow `action` on `resource` (and `record`). Never throws. */
export function canAccess({ permissions, resource, action, record }: AccessCheck): boolean {
  if (!permissions || permissions.length === 0) return false
  try {
    const valid = permissions.filter(isPermission)
    // Any matching deny wins over every allow.
    if (valid.some((p) => p.type === 'deny' && matchTarget(p, resource, action, record))) {
      return false
    }
    return valid.some((p) => p.type !== 'deny' && matchTarget(p, resource, action, record))
  } catch {
    return false
  }
}

function matchTarget(
  permission: Permission,
  resource: string,
  action: string | undefined,
  record: unknown
): boolean {
  const resources = Array.isArray(permission.resource) ? permission.resource : [permission.resource]
  if (!resources.some((pattern) => matchWildcard(pattern, resource))) return false
  if (action == null || action === '*') return true
  if (Array.isArray(permission.action)) {
    if (!permission.action.includes(action)) return false
  } else if (permission.action !== '*' && permission.action !== action) {
    return false
  }
  if (permission.record && record) {
    if (!isMatch(record, permission.record)) return false
  }
  return true
}

function matchWildcard(pattern: string, resource: string): boolean {
  if (pattern === '*' || pattern === resource) return true
  if (pattern.endsWith('*')) return resource.startsWith(pattern.slice(0, -1))
  return false
}

/**
 * lodash `isMatch(object, source)`: a partial deep comparison. Every key of
 * `source` must be present in `object` with a partially equal value; arrays
 * in `source` match when each element matches a distinct element of the
 * (possibly longer) array in `object`, in any order.
 */
export function isMatch(object: unknown, source: object): boolean {
  const keys = Object.keys(source)
  if (object == null) return keys.length === 0
  const target = Object(object) as Record<string, unknown>
  const src = source as Record<string, unknown>
  return keys.every((key) => key in target && partialEqual(src[key], target[key]))
}

function partialEqual(src: unknown, obj: unknown): boolean {
  if (src === obj) return true
  if (Number.isNaN(src) && Number.isNaN(obj)) return true
  if (typeof src !== 'object' || typeof obj !== 'object' || src === null || obj === null) {
    return false
  }
  if (Array.isArray(src) || Array.isArray(obj)) {
    if (!Array.isArray(src) || !Array.isArray(obj) || src.length > obj.length) return false
    const seen = new Set<number>()
    return src.every((item: unknown) => {
      const index = obj.findIndex((other: unknown, i) => !seen.has(i) && partialEqual(item, other))
      if (index < 0) return false
      seen.add(index)
      return true
    })
  }
  if (src instanceof Date || obj instanceof Date) {
    return src instanceof Date && obj instanceof Date && src.getTime() === obj.getTime()
  }
  if (Object.prototype.toString.call(src) !== Object.prototype.toString.call(obj)) return false
  return isMatch(obj, src)
}
